import { Card, Form, InputNumber, Select, Space, Typography } from 'antd';
import {
  CloseOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import type { InlineTriggerFormValues } from '~/app/components/maintenance/preventive/wizard/StepTasks/types';
import { useStepTasksTriggerBuilderOptions } from '~/app/components/maintenance/preventive/wizard/StepTasks/components/StepTasksTriggerBuilder/hooks/useStepTasksTriggerBuilderOptions';
import {
  canAddTriggerCondition,
  conditionTypeOptionsForRow,
  createEmptyTriggerCondition,
  intervalUnitsForConditionType,
  MAX_TRIGGER_CONDITIONS,
  type TriggerConditionFormValue,
} from '~/app/components/maintenance/tasks-triggers/utils';

const { Paragraph } = Typography;

type StepTasksTriggerBuilderProps = {
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: InlineTriggerFormValues) => Promise<void>;
};

export function StepTasksTriggerBuilder({
  loading,
  onCancel,
  onSubmit,
}: StepTasksTriggerBuilderProps) {
  const { t } = useTranslation('maintenance');
  const [form] = Form.useForm<InlineTriggerFormValues>();
  const { basisOptions, conditionTypeOptions } =
    useStepTasksTriggerBuilderOptions();

  return (
    <Card
      size="small"
      title={t('wizard.tasks.createTriggerTitle')}
      style={{ margin: '0 16px 12px', background: '#fafafa' }}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => {
            form.resetFields();
            onCancel();
          }}
          aria-label={t('wizard.cancel')}
        />
      }
      styles={{ body: { paddingTop: 12 } }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ conditions: [createEmptyTriggerCondition()] }}
        onFinish={(values) => {
          void onSubmit(values).then(() => form.resetFields());
        }}
      >
        <Form.Item
          name="basis"
          label={t('triggers.basis')}
          rules={[{ required: true }]}
        >
          <Select
            options={basisOptions}
            placeholder={t('triggers.selectBasis')}
          />
        </Form.Item>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {t('triggers.conditionsHint', { max: MAX_TRIGGER_CONDITIONS })}
        </Paragraph>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {t('wizard.tasks.existingTriggersHint')}
        </Paragraph>
        <Form.List name="conditions">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Card
                  key={field.key}
                  size="small"
                  style={{ marginBottom: 12 }}
                  extra={
                    fields.length > 1 ? (
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    ) : null
                  }
                >
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const conditions = getFieldValue('conditions') as
                        | TriggerConditionFormValue[]
                        | undefined;
                      return (
                        <Form.Item
                          name={[field.name, 'conditionType']}
                          label={t('triggers.conditionType')}
                          rules={[{ required: true }]}
                        >
                          <Select
                            options={conditionTypeOptionsForRow(
                              conditionTypeOptions,
                              conditions,
                              field.name,
                            )}
                            onChange={() => {
                              form.setFieldValue(
                                ['conditions', field.name, 'intervalUnit'],
                                undefined,
                              );
                            }}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const conditions = getFieldValue('conditions') as
                        | TriggerConditionFormValue[]
                        | undefined;
                      const conditionType =
                        conditions?.[field.name]?.conditionType;
                      return (
                        <Form.Item
                          name={[field.name, 'intervalUnit']}
                          label={t('triggers.intervalUnit')}
                          rules={[{ required: true }]}
                        >
                          <Select
                            disabled={!conditionType}
                            options={intervalUnitsForConditionType(
                              conditionType,
                            ).map((unit) => ({
                              label: t(`intervalUnit.${unit}`),
                              value: unit,
                            }))}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'intervalValue']}
                    label={t('triggers.intervalValue')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Card>
              ))}
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const conditions = getFieldValue('conditions') as
                    | TriggerConditionFormValue[]
                    | undefined;
                  const allowAdd = canAddTriggerCondition(conditions);
                  return (
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      disabled={!allowAdd}
                      onClick={() => add(createEmptyTriggerCondition())}
                      style={{ marginBottom: 16 }}
                    >
                      {allowAdd
                        ? t('triggers.addCondition')
                        : t('triggers.addConditionDisabled')}
                    </Button>
                  );
                }}
              </Form.Item>
            </>
          )}
        </Form.List>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            {t('wizard.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('wizard.tasks.createTrigger')}
          </Button>
        </Space>
      </Form>
    </Card>
  );
}
